import React from "react";

/**
 * Toolbar — chip container with optional heading, group divider, and layout.
 *
 * Props:
 *   layout   = "row" | "grid" (default "row")
 *   columns  = number for grid (default 4)
 *   label    = tiny header string
 *   accent   = "gold" | "cyan" (label color)
 *   sticky   = boolean (adds sticky sci-fi glass surface)
 *   compact  = tighter padding for dense areas
 */
export function Toolbar({
  layout = "row",
  columns = 4,
  label,
  accent = "gold",
  sticky = false,
  compact = false,
  children,
  className = "",
  testId,
}) {
  const labelColor = accent === "cyan" ? "text-[#00E5FF]" : "text-[#F2C94C]";
  const containerCls = [
    "relative rounded-3xl border overflow-hidden",
    "bg-gradient-to-br from-[#0e0b13]/85 via-[#0a0812]/85 to-[#0a0a0a]/85",
    "border-white/[0.06]",
    "backdrop-blur-xl",
    compact ? "p-3" : "p-4",
    sticky ? "sticky top-4 z-10" : "",
    className,
  ].filter(Boolean).join(" ");

  return (
    <div className={containerCls} data-testid={testId}>
      {/* soft aurora overlay */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          background:
            "radial-gradient(120% 60% at 10% 0%, rgba(242,201,76,0.06), transparent 60%), " +
            "radial-gradient(120% 60% at 100% 100%, rgba(0,229,255,0.06), transparent 60%)",
        }}
      />
      {label && (
        <div className={`relative font-mono-hy text-[9px] tracking-[0.24em] mb-3 ${labelColor}`}>
          {label}
        </div>
      )}
      <div
        className={
          "relative " +
          (layout === "grid"
            ? `grid gap-2`
            : "flex flex-wrap items-stretch gap-2")
        }
        style={layout === "grid" ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` } : undefined}
      >
        {children}
      </div>
    </div>
  );
}

export default Toolbar;
